import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import { useTelegram } from './hooks/useTelegram.js';
import { useMining } from './hooks/useMining.js';
import { api } from './services/api.js';
import { sound } from './utils/sound.js';

import { Navbar } from './components/Navbar.js';
import { Navigation, type ScreenTab } from './components/Navigation.js';
import { FloatingCoinsEffect } from './components/common/FloatingCoinsEffect.js';
import { CoinStreamEffect } from './components/common/CoinStreamEffect.js';
import { MinerActivationEffect } from './components/common/MinerActivationEffect.js';
import { AmbientBackground } from './components/common/AmbientBackground.js';
import { StarsShopModal } from './components/StarsShopModal.js';
import { NovaCoin3D } from './components/common/NovaCoin3D.js';

// Screens
import { HomeScreen } from './screens/HomeScreen.js';
import { MainMiningScreen } from './screens/MainMiningScreen.js';
import { AutoMinerScreen } from './screens/AutoMinerScreen.js';
import { StarsMinerScreen } from './screens/StarsMinerScreen.js';
import { ReferralScreen } from './screens/ReferralScreen.js';
import { ActivityScreen } from './screens/ActivityScreen.js';
import { ProfileScreen } from './screens/ProfileScreen.js';

import type { TaskDto, UserProfileResponse } from '@nova-mine/shared';

export function App() {
  const { isReady, initData, user, startParam, triggerHaptic, openInvoice, openTelegramLink } =
    useTelegram();

  const [activeTab, setActiveTab] = useState<ScreenTab>('home');
  const [isStarsShopOpen, setIsStarsShopOpen] = useState(false);
  const [floatingCoinsKey, setFloatingCoinsKey] = useState(0);
  const [coinStreamKey, setCoinStreamKey] = useState(0);
  const [minerActivatedKey, setMinerActivatedKey] = useState(0);
  const [tasks, setTasks] = useState<TaskDto[]>([]);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  // Mining hook
  const {
    profile,
    isLoading: isProfileLoading,
    isClaiming,
    claimSuccessMessage,
    liveAccrued,
    liveMainAccrued,
    liveAutoAccrued,
    liveStarsAccrued,
    claim,
    refresh,
  } = useMining(triggerHaptic);

  // Trigger floating coin burst
  const triggerCoinBurst = useCallback(() => {
    setFloatingCoinsKey((prev) => prev + 1);
  }, []);

  // Fetch tasks
  const loadTasks = useCallback(async () => {
    try {
      const data = await api.getTasks();
      setTasks(data.tasks);
    } catch {
      // In development fallback to sample tasks
      if (!import.meta.env.PROD) {
        setTasks([
          {
            id: 1,
            task_type: 'website_view',
            title: 'Visit Nova Mine Announcement Channel',
            reward_coins: 50,
            url: 'https://t.me/NovaMineAnnouncements',
            status: 'available',
            completed_at: null,
          },
          {
            id: 2,
            task_type: 'website_view',
            title: 'Explore Edge Architecture Docs',
            reward_coins: 50,
            url: 'https://docs.turso.tech',
            status: 'available',
            completed_at: null,
          },
          {
            id: 3,
            task_type: 'website_view',
            title: 'Review Nova Mine Whitepaper',
            reward_coins: 50,
            url: 'https://novamine.app/whitepaper',
            status: 'available',
            completed_at: null,
          },
        ]);
      } else {
        setTasks([]);
      }
    }
  }, []);

  // Authenticate and load profile
  const init = useCallback(async () => {
    setAuthError(null);
    setIsRetrying(true);
    try {
      await api.loginTelegram(initData, startParam);
      await refresh();
      await loadTasks();
      setIsDemoMode(false);
    } catch (err: unknown) {
      console.warn('[App Init] Live API unreachable or authentication failed.', err);
      if (import.meta.env.PROD) {
        // Strictly forbid demo mode and fake balances in production
        setIsDemoMode(false);
        const msg = err instanceof Error ? err.message : 'Failed to connect to Nova Mine Core API.';
        setAuthError(`${msg} Please ensure you are opening within Telegram with an active connection.`);
      } else {
        // Local standalone browser testing sandbox only
        setIsDemoMode(true);
        await loadTasks();
      }
    } finally {
      setIsRetrying(false);
    }
  }, [initData, startParam, refresh, loadTasks]);

  useEffect(() => {
    if (!isReady) return;
    init();
  }, [isReady, init]);

  // Production safety: Never show demo profile in production
  const effectiveProfile: UserProfileResponse | null = profile || (!import.meta.env.PROD && isDemoMode ? {
    id: 1,
    telegram_id: user?.id || 88888888,
    username: user?.username || 'novapioneer',
    first_name: user?.first_name || 'Nova Pioneer',
    last_name: null,
    referral_code: 'NOVADEMO1',
    balance: 1450.5,
    mining: {
      main: {
        active: true,
        daily_rate: 2000,
        rate_per_second: 2000 / 86400,
        last_claimed_at: Date.now() - 14 * 3600 * 1000,
        accrued_coins: (14 * 3600 * 2000) / 86400,
      },
      auto: {
        unlocked: false,
        active: false,
        daily_rate: 2000,
        rate_per_second: 2000 / 86400,
        last_claimed_at: Date.now(),
        accrued_coins: 0,
        completed_tasks_count: 1,
        required_tasks: 3,
      },
      stars: {
        active: false,
        daily_rate: 0,
        rate_per_second: 0,
        last_claimed_at: Date.now(),
        accrued_coins: 0,
        total_units: 0,
        miners: [],
      },
      aggregate: {
        total_rate_per_second: 2000 / 86400,
        total_daily_rate: 2000,
        total_accrued: (14 * 3600 * 2000) / 86400,
        server_time: Date.now(),
      },
    },
    referrals: {
      count: 2,
      total_reward: 20000,
    },
  } : null);

  const handleClaimCoins = async (
    type: 'all' | 'main' | 'auto' | 'stars' = 'all',
    minerId?: string
  ) => {
    if (isDemoMode) {
      alert(`DEMO MODE: Simulated claim of ${type.toUpperCase()} miner coins. Connect live backend for permanent ledger entries.`);
      return { claimed_amount: 50 };
    }
    const res = await claim(type, minerId);
    // Real server confirmation only:
    sound.playClaimSuccess();
    triggerCoinBurst();
    setCoinStreamKey((prev) => prev + 1);
    return res;
  };

  // 1. Error state when live API fails in production or cannot authenticate
  if (authError && !effectiveProfile) {
    return (
      <div className="min-h-screen bg-midnight-950 flex flex-col items-center justify-center p-6 text-center text-white relative">
        <AmbientBackground />
        <div className="relative z-10 max-w-sm w-full bg-midnight-900/90 border border-red-500/30 rounded-2xl p-6 backdrop-blur-xl shadow-2xl">
          <div className="w-14 h-14 mx-auto rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-400 mb-4">
            <AlertTriangle className="w-7 h-7" />
          </div>
          <h2 className="text-lg font-black tracking-wide uppercase text-white">Connection Error</h2>
          <p className="mt-2 text-xs text-gray-400 leading-relaxed font-sans">
            {authError}
          </p>
          <div className="mt-4 p-2.5 rounded-lg bg-midnight-950/70 border border-white/5 text-[11px] text-gray-400 font-mono text-left">
            🔒 Production security: Simulated balances and demo accounts are blocked.
          </div>
          <button
            onClick={() => init()}
            disabled={isRetrying}
            className="mt-5 w-full py-3 px-4 rounded-xl font-bold text-xs uppercase tracking-wider bg-gradient-to-r from-nova-purple to-nova-cyan text-white shadow-lg hover:brightness-110 active:scale-95 transition-all flex items-center justify-center gap-2"
          >
            {isRetrying ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Connecting...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                Retry Connection
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  // 2. Loading state while waiting for Telegram or initial profile
  if (!effectiveProfile || (isProfileLoading && !profile && !isDemoMode)) {
    return (
      <div className="min-h-screen bg-midnight-950 flex flex-col items-center justify-center p-6 text-center">
        <NovaCoin3D size="xl" animate={true} />
        <h1 className="mt-4 text-xl font-black tracking-wider uppercase text-gold-gradient">
          Nova Mine
        </h1>
        <p className="mt-1 text-xs text-gray-400">Initializing VIP Cloud Miner...</p>
        <Loader2 className="mt-6 w-5 h-5 animate-spin text-nova-cyan" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-midnight-950 text-white flex flex-col relative selection:bg-nova-cyan/20 selection:text-nova-cyan pb-28">
      {/* Ambient Lighting & Particles */}
      <AmbientBackground />

      {/* Floating Coins Burst FX */}
      <FloatingCoinsEffect triggerKey={floatingCoinsKey} />

      {/* Upward Coin Stream to Balance Indicator on Server-Confirmed Claim */}
      <CoinStreamEffect triggerKey={coinStreamKey} />

      {/* Golden Shockwave on Verified Stars Miner Activation */}
      <MinerActivationEffect triggerKey={minerActivatedKey} />

      {/* Sticky Header */}
      <Navbar
        username={effectiveProfile.username}
        firstName={effectiveProfile.first_name}
        balance={effectiveProfile.balance}
        isDemoMode={isDemoMode}
        onOpenProfile={() => setActiveTab('profile')}
      />

      {/* Main Screen Container with Page Transitions */}
      <main className="flex-1 w-full max-w-lg mx-auto px-4 py-4 relative z-10 pb-36">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
          >
            {activeTab === 'home' && (
              <HomeScreen
                profile={effectiveProfile}
                liveAccrued={liveAccrued || effectiveProfile.mining.aggregate.total_accrued}
                isClaiming={isClaiming}
                onClaimAll={() => handleClaimCoins('all')}
                onNavigate={(tab) => setActiveTab(tab)}
              />
            )}

            {activeTab === 'main' && (
              <MainMiningScreen
                mainAccrued={liveMainAccrued || effectiveProfile.mining.main.accrued_coins}
                lastClaimedAtMs={effectiveProfile.mining.main.last_claimed_at}
                isClaiming={isClaiming}
                claimSuccessMessage={claimSuccessMessage}
                onClaim={() => handleClaimCoins('main')}
                onTriggerFloatingCoins={triggerCoinBurst}
                onBoostCompleted={refresh}
                accountBalance={effectiveProfile.balance}
                onSwitchToAuto={() => setActiveTab('auto')}
              />
            )}

            {activeTab === 'auto' && (
              <AutoMinerScreen
                autoUnlocked={effectiveProfile.mining.auto.unlocked}
                autoAccrued={liveAutoAccrued || effectiveProfile.mining.auto.accrued_coins}
                lastClaimedAtMs={effectiveProfile.mining.auto.last_claimed_at}
                tasks={tasks}
                isClaiming={isClaiming}
                claimSuccessMessage={claimSuccessMessage}
                onClaim={() => handleClaimCoins('auto')}
                onTriggerFloatingCoins={triggerCoinBurst}
                onRefreshTasks={loadTasks}
                accountBalance={effectiveProfile.balance}
                onSwitchToMain={() => setActiveTab('main')}
              />
            )}

            {activeTab === 'stars' && (
              <StarsMinerScreen
                starsUnits={effectiveProfile.mining.stars.total_units}
                starsAccrued={liveStarsAccrued || effectiveProfile.mining.stars.accrued_coins}
                minersList={effectiveProfile.mining.stars.miners}
                lastClaimedAtMs={effectiveProfile.mining.stars.last_claimed_at}
                isClaiming={isClaiming}
                claimSuccessMessage={claimSuccessMessage}
                onOpenShop={() => setIsStarsShopOpen(true)}
                onClaimAllStars={() => handleClaimCoins('stars')}
                onClaimSingleMiner={(minerId) => handleClaimCoins('stars', minerId)}
                onTriggerFloatingCoins={triggerCoinBurst}
                openInvoice={openInvoice}
                onPurchaseSuccess={refresh}
                onMinerActivated={() => setMinerActivatedKey((prev) => prev + 1)}
                accountBalance={effectiveProfile.balance}
                isDemoMode={isDemoMode}
              />
            )}

            {activeTab === 'referrals' && (
              <ReferralScreen
                referralCode={effectiveProfile.referral_code}
                referralCount={effectiveProfile.referrals.count}
                totalReward={effectiveProfile.referrals.total_reward}
                openTelegramLink={openTelegramLink}
              />
            )}

            {activeTab === 'activity' && (
              <ActivityScreen
                balance={effectiveProfile.balance}
                starsMinersCount={effectiveProfile.mining.stars.total_units}
                referralCount={effectiveProfile.referrals.count}
              />
            )}

            {activeTab === 'profile' && (
              <ProfileScreen
                profile={effectiveProfile}
                user={user}
                isDemoMode={isDemoMode}
                onToggleDemoMode={() => setIsDemoMode(!isDemoMode)}
                onNavigateToActivity={() => setActiveTab('activity')}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Stars Shop Multi-Purchase Modal */}
      <StarsShopModal
        isOpen={isStarsShopOpen}
        onClose={() => setIsStarsShopOpen(false)}
        onSuccess={() => {
          setMinerActivatedKey((prev) => prev + 1);
          refresh();
        }}
        openInvoice={openInvoice}
        isDemoMode={isDemoMode}
      />

      {/* Bottom Sticky Navigation */}
      <Navigation
        activeTab={activeTab}
        onSelectTab={(tab) => setActiveTab(tab)}
        triggerHaptic={triggerHaptic}
      />
    </div>
  );
}
